/** Inactivity-based auto-logout with a 1-minute pre-warning.
 * Watches mouse/keyboard/touch/scroll and resets a timer.
 * Used to enforce HIPAA-aware session policy for field agents.  */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Clock4, ShieldAlert } from "lucide-react";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "click", "mousemove"];

/**
 * @param idleSeconds {number} how long after the LAST event to fully sign out (default 480s = 8 min)
 * @param warnAtRemaining {number} how many seconds before logout to show the countdown warning (default 60s)
 */
export default function IdleLogout({ idleSeconds = 480, warnAtRemaining = 60 }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const lastActivity = useRef(Date.now());
  const [warning, setWarning] = useState(null); // seconds remaining

  useEffect(() => {
    const onActivity = () => {
      lastActivity.current = Date.now();
      if (warning !== null) setWarning(null);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivity.current) / 1000);
      const remaining = idleSeconds - elapsed;
      if (remaining <= 0) {
        logout();
        nav("/login", { replace: true });
      } else if (remaining <= warnAtRemaining) {
        setWarning(remaining);
      } else if (warning !== null) {
        setWarning(null);
      }
    }, 1000);
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(tick);
    };
  }, [idleSeconds, warnAtRemaining, logout, nav, warning]);

  if (warning === null) return null;
  const mm = Math.floor(warning / 60);
  const ss = warning % 60;
  return (
    <div
      data-testid="idle-warning"
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[95] bg-[#15233B]/70 backdrop-blur-md grid place-items-center p-4"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 bg-[#b91c1c] text-white flex items-center gap-3">
          <ShieldAlert className="h-5 w-5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/85">Session security</div>
            <h2 className="text-base font-extrabold tracking-tight">Are you still there, {user?.name || "agent"}?</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#15233B]">
            You'll be signed out in <b className="text-[#b91c1c] tabular-nums">{mm}:{String(ss).padStart(2, "0")}</b> due to inactivity. This protects the PHI on your screen.
          </p>
          <div className="bg-[#FFF9C4] border border-[#FBBF24] rounded-md p-3 text-xs text-[#15233B] flex items-start gap-2">
            <Clock4 className="h-4 w-4 text-[#b91c1c] shrink-0 mt-0.5" />
            Move the mouse, touch the screen, or press any key to stay signed in.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#15233B]/10 bg-[#F2F2F2] flex items-center gap-2">
          <button
            data-testid="idle-signout-now"
            onClick={() => { logout(); nav("/login", { replace: true }); }}
            className="px-3 py-2 rounded text-sm font-semibold text-[#b91c1c] hover:bg-[#b91c1c]/5"
          >
            Sign out now
          </button>
          <button
            data-testid="idle-stay"
            autoFocus
            onClick={() => { lastActivity.current = Date.now(); setWarning(null); }}
            className="ml-auto px-4 py-2 rounded bg-[#15233B] hover:bg-[#1E73C9] text-white text-sm font-extrabold"
          >
            I'm still here
          </button>
        </div>
      </div>
    </div>
  );
}
